/* eslint-disable no-plusplus */
import { faUpload as faUploadReg } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DragEvent, ReactNode, useRef, useState } from 'react';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';

const Overlay = styled.div`
    position: absolute;
    inset: 0;
    z-index: 100;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    background-color: color-mix(in srgb, ${tokens.color.navy900} 20%, transparent);
    border: 3px dashed ${tokens.border.focus};
    border-radius: 8px;
    pointer-events: none;
    color: ${tokens.text.primary};
    font-size: 1.25rem;
    font-weight: 500;
`;

type UseDragDropFilesParams = {
    onFiles: (files: File[]) => void;
    folderName?: string;
    disabled?: boolean;
};

type UseDragDropFilesResult = {
    dragProps: {
        onDragEnter: (e: DragEvent<HTMLElement>) => void;
        onDragLeave: () => void;
        onDragOver: (e: DragEvent<HTMLElement>) => void;
        onDrop: (e: DragEvent<HTMLElement>) => void;
    };
    overlay: ReactNode;
};

export function useDragDropFiles({
    onFiles,
    folderName = 'root',
    disabled = false,
}: UseDragDropFilesParams): UseDragDropFilesResult {
    const [isDragOver, setIsDragOver] = useState(false);
    // Using a counter instead of a boolean because dragenter/dragleave fire for
    // every child element, which means entering a child fires leave on the
    // parent and a boolean would flicker off mid-drag.
    // seealso: https://www.geeksforgeeks.org/html/how-to-dragleave-fired-when-hovering-a-child-element-in-html-5/
    const dragCounter = useRef(0);

    const onDragEnter = (e: DragEvent<HTMLElement>) => {
        if (disabled) {
            return;
        }
        if (!e.dataTransfer.types.includes('Files')) {
            return;
        }
        dragCounter.current++;
        setIsDragOver(true);
    };

    const onDragLeave = () => {
        if (dragCounter.current > 0) {
            dragCounter.current--;
        }
        if (dragCounter.current === 0) {
            setIsDragOver(false);
        }
    };

    const onDragOver = (e: DragEvent<HTMLElement>) => {
        if (disabled) {
            return;
        }
        e.preventDefault();
    };

    const onDrop = (e: DragEvent<HTMLElement>) => {
        e.preventDefault();
        dragCounter.current = 0;
        setIsDragOver(false);
        if (disabled) {
            return;
        }
        onFiles(Array.from(e.dataTransfer.files));
    };

    const overlay = isDragOver ? (
        <Overlay>
            <FontAwesomeIcon icon={faUploadReg} size="3x" />
            Drop to upload to <strong>{folderName}</strong>
        </Overlay>
    ) : null;

    return {
        dragProps: { onDragEnter, onDragLeave, onDragOver, onDrop },
        overlay,
    };
}
